import { ScoreType, ScoreConfig } from "@/pages/deals/scoreConfig";
import ScoreGuardrailRow from "@/pages/settings/components/dealsSettings/StageStudio/workflow/components/ScoreGuardrailRow";
import { Divider } from "@/components/ui/divider";
import Text from "@/components/ui/text";
import {
  useThresholdById,
  useUpdateThresholdHigh,
  useUpdateThresholdMedium
} from "@/entities/settings/hooks";
import { SettingsThresholdsType } from "@/entities/settings/types";
import { ceil } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const TRANSLATION_PREFIX =
  "settings.deals.workflow.workflowDetailedView.scoreGuardrails";

interface SimpleGuardrailsProps {
  kpiValue: ScoreType;
  totalFields: number;
}

const ScoreGuardrails: React.FC<SimpleGuardrailsProps> = ({
  kpiValue,
  totalFields
}) => {
  const { t } = useTranslation();

  const { data: thresholdData, isLoading: isLoadingThreshold } =
    useThresholdById(SettingsThresholdsType.CHAMPION_SCORE);
  const updateMediumThresholdMutation = useUpdateThresholdMedium(
    SettingsThresholdsType.CHAMPION_SCORE
  );
  const updateHighThresholdMutation = useUpdateThresholdHigh(
    SettingsThresholdsType.CHAMPION_SCORE
  );

  const [mediumValue, setMediumValue] = useState("");
  const [highValue, setHighValue] = useState("");

  useEffect(() => {
    if (thresholdData?.medium !== undefined) {
      setMediumValue(thresholdData.medium.toString());
    }
    if (thresholdData?.high !== undefined) {
      setHighValue(thresholdData.high.toString());
    }
  }, [thresholdData?.medium, thresholdData?.high]);

  const defaultRange: [number, number] = [0, 0];

  const processNumFieldsRanges = useMemo((): Record<
    number,
    [number, number]
  > => {
    if (!mediumValue || !highValue || !totalFields) {
      return {
        0: defaultRange,
        1: defaultRange,
        2: defaultRange
      };
    }

    const medium = parseInt(mediumValue);
    const high = parseInt(highValue);

    const pendingPercentage = high - medium;

    const failedFields = ceil((medium / 100) * totalFields);
    const pendingFields = ceil((pendingPercentage / 100) * totalFields);

    const ranges: Record<number, [number, number]> = {
      0: [0, failedFields],
      1: [failedFields + 1, failedFields + pendingFields],
      2: [failedFields + pendingFields + 1, totalFields]
    };

    return ranges;
  }, [mediumValue, highValue, totalFields]);

  const handleMediumPercentageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    // Allow empty string or numbers from 0-100
    if (
      value === "" ||
      (/^\d+$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 100)
    ) {
      setMediumValue(value);
    }
  };

  const handleHighPercentageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    // Allow empty string or numbers from 0-100
    if (
      value === "" ||
      (/^\d+$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 100)
    ) {
      setHighValue(value);
    }
  };

  const handleMediumPercentageBlur = () => {
    if (mediumValue !== "" && thresholdData) {
      const newMediumValue = parseInt(mediumValue);
      if (newMediumValue !== thresholdData.medium) {
        updateMediumThresholdMutation.mutate({
          data: { medium: newMediumValue }
        });
      }
    }
  };
  const handleHighPercentageBlur = () => {
    if (highValue !== "" && thresholdData) {
      const newHighValue = parseInt(highValue);
      if (newHighValue !== thresholdData.high) {
        updateHighThresholdMutation.mutate({
          data: { high: newHighValue }
        });
      }
    }
  };

  const handleForceUpdateMedium = (value: number) => {
    setMediumValue(value.toString());
    updateMediumThresholdMutation.mutate({
      data: { medium: value }
    });
  };

  const handleForceUpdateHigh = (value: number) => {
    setHighValue(value.toString());
    updateHighThresholdMutation.mutate({
      data: { high: value }
    });
  };

  return (
    <div className="flex flex-col">
      <div className="flex p-2 justify-between items-center">
        <Text variant="bodySecondary">
          {t(`${TRANSLATION_PREFIX}.percentage`)}
        </Text>
        <div className="w-40">
          <Text variant="bodySecondary">
            {t(`${TRANSLATION_PREFIX}.score`)}
          </Text>
        </div>
      </div>
      <div className="rounded flex flex-col gap-1 border border-border-border1">
        <ScoreGuardrailRow
          kpiValue={kpiValue}
          scoreText={ScoreConfig.FAILED}
          disabledInputValue={0}
          fieldsNumRange={processNumFieldsRanges[0] || defaultRange}
          isLoadingThreshold={isLoadingThreshold}
          isUpdating={updateMediumThresholdMutation.isPending}
          inputValue={mediumValue}
          handleInputChange={handleMediumPercentageChange}
          handleInputBlur={handleMediumPercentageBlur}
          onForceUpdate={handleForceUpdateMedium}
          subText={t(`${TRANSLATION_PREFIX}.or`)}
          secondSubText={t(`${TRANSLATION_PREFIX}.oneFailed`)}
        />
        <Divider />
        <ScoreGuardrailRow
          kpiValue={kpiValue}
          scoreText={ScoreConfig.PENDING}
          fieldsNumRange={processNumFieldsRanges[1] || defaultRange}
          isLoadingThreshold={isLoadingThreshold}
          isUpdating={updateHighThresholdMutation.isPending}
          disabledInputValue={parseInt(mediumValue) + 1}
          inputValue={highValue}
          handleInputChange={handleHighPercentageChange}
          handleInputBlur={handleHighPercentageBlur}
          onForceUpdate={handleForceUpdateHigh}
        />
        <Divider />
        <ScoreGuardrailRow
          kpiValue={kpiValue}
          scoreText={ScoreConfig.PASS}
          fieldsNumRange={processNumFieldsRanges[2] || defaultRange}
          inputValue="100"
          disabledInputValue={parseInt(highValue) + 1}
          subText={t(`${TRANSLATION_PREFIX}.and`)}
          secondSubText={t(`${TRANSLATION_PREFIX}.allPassed`)}
        />
      </div>
    </div>
  );
};

export default ScoreGuardrails;
